import React, { useCallback, useMemo, useState } from 'react'
import {
  DocumentRequest,
  DOCUMENT_REQUEST_STATUS,
} from '@trustero/trustero-api-web/lib/request/request_pb'
import { useBasicDropdown } from 'src/components/Reusable/BasicDropdown/context/BasicDropdownContext'
import { BasicDropdown } from 'src/components/Reusable/BasicDropdown/BasicDropdown'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import { DOCUMENT_REQUEST_STATUS_TO_LABEL } from 'src/pages/Requests/requests.constants'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import {
  useAddOrUpdateDocumentRequest,
  useUpdateDocumentRequest,
} from '../../../../../components/async/DocumentRequest/useDocumentRequests'
import { StatusDropdownContainer, RequestStatusStyles } from './styles'

type RequestStatusDropdownProps = {
  request: DocumentRequest
  isDisabled: boolean
  onSelect?: (status: DOCUMENT_REQUEST_STATUS) => void
}

export const RequestStatusDropdown = ({
  request,
  isDisabled = false,
  onSelect: onSelectProp,
}: RequestStatusDropdownProps): JSX.Element => {
  const newModel = useNewModel()
  const updateDocumentRequestNewModel = useUpdateDocumentRequest()
  const updateDocumentRequest = useAddOrUpdateDocumentRequest()
  const currentStatus = request.getStatus()

  const { openDropdownId, setOpenDropdownId } = useBasicDropdown()
  const dropdownId = `request-status-dropdown-${request.getId()}`
  const isMenuOpen = useMemo(
    () => openDropdownId === dropdownId,
    [openDropdownId, dropdownId],
  )
  const [selectedItem, setSelectedItem] = useState<BasicDropdownItem>({
    name: DOCUMENT_REQUEST_STATUS_TO_LABEL[currentStatus],
    value: currentStatus,
  })
  const options = useMemo(() => {
    return Object.values(DOCUMENT_REQUEST_STATUS).map((option) => {
      return {
        name: DOCUMENT_REQUEST_STATUS_TO_LABEL[
          option as DOCUMENT_REQUEST_STATUS
        ],
        value: option,
      } as BasicDropdownItem
    })
  }, [])
  const handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      setOpenDropdownId(dropdownId)
    } else {
      setOpenDropdownId(null)
    }
  }
  const background = useMemo(() => {
    return RequestStatusStyles[currentStatus as DOCUMENT_REQUEST_STATUS].bg
  }, [currentStatus])

  const selectStatus = useCallback(
    async (newStatus: number) => {
      if (newModel) {
        await updateDocumentRequestNewModel({
          id: request.getId(),
          status: newStatus,
        })
      } else {
        await updateDocumentRequest({
          id: request.getId(),
          status: newStatus,
        })
      }
    },
    [newModel, updateDocumentRequestNewModel, updateDocumentRequest, request],
  )

  const handleSelect = (item: BasicDropdownItem) => {
    setSelectedItem(item)
    if (onSelectProp) {
      onSelectProp(item.value as DOCUMENT_REQUEST_STATUS)
    } else {
      selectStatus(item.value as DOCUMENT_REQUEST_STATUS)
    }
  }

  return (
    <StatusDropdownContainer>
      <BasicDropdown
        selectedItem={selectedItem}
        onSelect={handleSelect}
        items={options}
        show={isMenuOpen}
        onToggle={handleToggle}
        disabled={isDisabled}
        backgroundColor={background}
      />
    </StatusDropdownContainer>
  )
}
